import { useEffect, useState } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical } from 'lucide-react'
import { Button } from '@components/v2/button'
import { Modal } from '@components/v2/modal'
import ListIcon from './list-icon.svg'
import { t } from '@lingui/core/macro'

interface ListItem {
  id: string | number
  name: string
}

const SortableItem = props => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: props.item.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <li ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <span
        className="inline-block font-bold border border-cv-blue-200 pl-1 pr-3 py-1 mb-2"
        style={{ boxShadow: '1px 1px 4px 0px rgba(0, 0, 0, 0.1)' }}
      >
        <GripVertical size={18} color="#7795A1" className="inline-block" />
        <ListIcon className="inline-block mr-2" />
        {props.item.name}
      </span>
    </li>
  )
}

export function ExportModal({ isOpen, setIsOpen, selectedLists }) {
  const [items, setItems] = useState<ListItem[]>([])

  useEffect(() => {
    setItems(selectedLists)
  }, [selectedLists])

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleDragEnd = event => {
    const { active, over } = event

    if (active.id !== over.id) {
      setItems(items => {
        const oldIndex = items.findIndex(item => item.id === active.id)
        const newIndex = items.findIndex(item => item.id === over.id)

        return arrayMove(items, oldIndex, newIndex)
      })
    }
  }

  const handleDownload = () => {
    console.log(`Downloading...`)
  }

  return (
    <Modal.Container
      isOpen={isOpen}
      onClickOutside={() => setIsOpen(false)}
      className="max-w-[640px]"
    >
      <div className="px-10 pt-8 grid grid-cols-2 max-h-[388px] overflow-hidden">
        <div>
          <h2 className="text-[18px] font-semibold">{t`Customize your export`}</h2>
          <p className="text-[13px] mt-2 text-cv-blue-800">
            {t`Streamline post-export workflows and enhances data usability.`}
          </p>
          <p className="text-[13px] mt-4 text-cv-blue-800 mb-4">
            Drag and drop the lists to arrange them in your preferred order. This sequence
            determines the structure of your exported CSV file, ensuring your data is organized
            exactly as you need it.
          </p>
        </div>
        <div className="overflow-y-scroll max-h-[250px] px-8">
          <ul>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={items} strategy={verticalListSortingStrategy}>
                {items.map(item => (
                  <SortableItem key={item.id} item={item} />
                ))}
              </SortableContext>
            </DndContext>
          </ul>
        </div>
      </div>
      <div className="flex justify-between items-center border border-t-cv-blue-200 bg-cv-blue-100 py-3 px-4">
        <div>
          <Button variant="dark" onClick={handleDownload} className="mr-4">
            {t`Download`}
          </Button>
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            {t`Cancel`}
          </Button>
        </div>
        <span className="text-cv-500">
          {selectedLists?.length} {t`Lists selected`}
        </span>
      </div>
    </Modal.Container>
  )
}
